import { useEffect, lazy, Suspense } from "react";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import Hero from "@/components/Hero";

const AdminPage = lazy(() => import("@/components/AdminPage"));

const OnboardPage = lazy(() => import("@/components/OnboardPage"));
const ContractPage = lazy(() => import("@/components/ContractPage"));
const Projects = lazy(() => import("@/components/Projects"));
const OfferSection = lazy(() => import("@/components/OfferSection"));
const Manifesto = lazy(() => import("@/components/Manifesto"));
const About = lazy(() => import("@/components/About"));
const WhyUs = lazy(() => import("@/components/WhyUs"));
const Team = lazy(() => import("@/components/Team"));
const Insights = lazy(() => import("@/components/Insights"));
const Contact = lazy(() => import("@/components/Contact"));
const Footer = lazy(() => import("@/components/Footer"));
const OfferPopup = lazy(() => import("@/components/OfferPopup"));

export const scrollToSection = (id) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(id, { duration: 1.4, force: true });
  } else {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  }
};

function App() {
  useEffect(() => {
    let lenis = null;
    let rafId;
    const raf = (time) => {
      lenis?.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    const start = () => {
      lenis = new Lenis({ duration: 1.2, smoothWheel: true });
      window.__lenis = lenis;
      rafId = requestAnimationFrame(raf);
    };
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(start, { timeout: 1500 });
    } else {
      setTimeout(start, 800);
    }
    return () => {
      cancelAnimationFrame(rafId);
      lenis?.destroy();
      window.__lenis = null;
    };
  }, []);

  if (window.location.pathname.startsWith("/onboard/")) {
    const token = window.location.pathname.split("/onboard/")[1]?.replace(/\/+$/, "");
    return (
      <div className="bg-background text-foreground min-h-screen">
        <Suspense fallback={null}>
          <OnboardPage token={token} />
        </Suspense>
        <Toaster theme="dark" position="bottom-right" />
      </div>
    );
  }

  if (window.location.pathname.startsWith("/contract/")) {
    const token = window.location.pathname.split("/contract/")[1]?.replace(/\/+$/, "");
    return (
      <div className="bg-background text-foreground min-h-screen">
        <Suspense fallback={null}>
          <ContractPage token={token} />
        </Suspense>
        <Toaster theme="dark" position="bottom-right" />
      </div>
    );
  }

  if (window.location.pathname === "/admin") {
    return (
      <div className="bg-background text-foreground min-h-screen">
        <Suspense fallback={null}>
          <AdminPage />
        </Suspense>
        <Toaster theme="dark" position="bottom-right" />
      </div>
    );
  }

  return (
    <div className="bg-background text-foreground min-h-screen">
      <div className="noise-overlay" aria-hidden="true" />
      <Header />
      <main>
        <Hero />
        <Suspense fallback={null}>
          <Projects />
          <OfferSection />
          <Manifesto />
          <About />
          <WhyUs />
          <Team />
          <Insights />
          <Contact />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <Footer />
        <OfferPopup />
      </Suspense>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
